import React, { Suspense, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import cx from "classnames";

import { Asset, PreviewAsset } from "src/types/assets";

import styles from "./viewer.module.scss";
import { AUDIO_VIEWER, CLEVERSTORY_VIEWER, ENHANCED_VIEWER, HTML_APP_VIEWER, HTML_VIEWER, IMAGE_VIEWER, PDF_VIEWER, RENDER_IN_VIEWER, RENDER_OPTIMIZED_VIEW, SCORM_VIEWER, SPREADSHEET_VIEWER, VIDEO_VIEWER } from "src/constants/assets";
import AuthService from "src/utils/auth.service";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { triggerAssetProcessing } from "src/redux/actions/assets";
import { ElementTimerComponent } from "src/components/elementTimer";
import { useMediaQuery } from "react-responsive";

const DocumentViewer: React.FC<any> = React.lazy(() =>
  import("@paperflite-marketing/asset-viewer").then((module) => ({
    default: module.DocumentViewer,
  })),
);

const VideoViewer: React.FC<any> = React.lazy(() =>
  import("@paperflite-marketing/asset-viewer").then((module) => ({
    default: module.VideoViewer,
  })),
);

const HTMLViewer: React.FC<any> = React.lazy(() =>
  import("@paperflite-marketing/asset-viewer").then((module) => ({
    default: module.HTMLViewer,
  })),
);

const ScormViewer: React.FC<any> = React.lazy(() =>
  import("@paperflite-marketing/asset-viewer").then((module) => ({
    default: module.ScormViewer,
  })),
);

const ImageViewer: React.FC<any> = React.lazy(() =>
  import("@paperflite-marketing/asset-viewer").then((module) => ({
    default: module.ImageViewer,
  })),
);

const AudioViewer: React.FC<any> = React.lazy(() =>
  import("@paperflite-marketing/asset-viewer").then((module) => {
    return {
      default: module.AudioViewer,
    }
  }),
);

const AlbumCover = '/images/albumcover.jpg'

interface AssetViewerProps {
  className?: string;
  asset: Asset;
  scrollParent?: string;

  preferredWidth?: number;
  offset?: number;

  preview?: {
    loading: boolean;
    item: PreviewAsset | null;
  };

  fetchPreviewData?: () => void;

  // eslint-disable-next-line no-unused-vars
  recordSessionAnalytics?: (request: any) => void;

  // eslint-disable-next-line no-unused-vars
  onAnalyticsChange?: (data: any) => void;
}

export const AssetViewer: React.FC<AssetViewerProps> = ({
  className,
  asset,
  preferredWidth,
  offset,
  scrollParent,
  preview,
  fetchPreviewData,
  recordSessionAnalytics,
  onAnalyticsChange
}) => {

  const viewerType = useMemo(() => {
    return (asset?.viewer as string) || "unknown";
  }, [asset]);

  const processed = useMemo(() => {
    return ["OK", "ERROR"].includes(_.get(asset, "status") || "");
  }, [asset]);

  const processing = useSelector((state) => {
    const assetProcessing = _.get(state, "asset.process.processing", false);
    const assetMergeStatus = _.get(state, "asset.merge", {});
    const selectedAssetId = _.get(state, "asset.selectedAssetId", null);

    if (assetProcessing) {
      return true;
    }

    if (_.size(assetMergeStatus) > 0 && _.get(assetMergeStatus, "status") !== "OK") {
      return true;
    }

    if (_.size(selectedAssetId) > 0 && !assetProcessing) {
      return true;
    }

    const preparing = _.get(state, "asset.preparing", false);
    if (preparing) {
      return true;
    }

    return false;
  });

  const unknownViewer = useMemo(() => {
    return !_.includes([
      ENHANCED_VIEWER,
      PDF_VIEWER,
      VIDEO_VIEWER,
      IMAGE_VIEWER,
      SCORM_VIEWER,
      HTML_APP_VIEWER,
      AUDIO_VIEWER,
      HTML_VIEWER,
      CLEVERSTORY_VIEWER,
    ], viewerType);
  }, [viewerType]);


  return (
    <div className={cx(styles.assetViewer, className)}>

      {!processed && (
        <AssetProcessingStatus
          asset={asset}
          icon={'/images/assets/notification_empty_message.svg'}
          message={'Hang tight! Your file is processing...'}
        />
      )}

      {processed && [ENHANCED_VIEWER, PDF_VIEWER].includes(viewerType) && (
        <DocumentViewerComponent
          asset={asset}
          preferredWidth={preferredWidth}
          offset={offset}
          scrollParent={scrollParent}

          initialPage={0}
          processingAsset={false}
          preview={{
            ...preview || {
              loading: false,
              item: null,
            },
            loading: preview?.loading || processing,
          }}
          fetchPreviewData={fetchPreviewData}

          recordSessionAnalytics={recordSessionAnalytics}
          onAnalyticsChange={onAnalyticsChange}
        />
      )}

      {processed && viewerType === VIDEO_VIEWER && (
        <VideoViewerComponent
          asset={asset}
          onAnalyticsChange={onAnalyticsChange}
          recordSessionAnalytics={recordSessionAnalytics}
        />
      )}

      {processed && viewerType === IMAGE_VIEWER && (
        <ImageViewerComponent
          asset={asset}
          onAnalyticsChange={onAnalyticsChange}
          recordSessionAnalytics={recordSessionAnalytics}
        />
      )}

      {processed && [SCORM_VIEWER, HTML_APP_VIEWER].includes(viewerType) && (
        <ScromViewerComponent
          asset={asset}
          onAnalyticsChange={onAnalyticsChange}
          recordSessionAnalytics={recordSessionAnalytics}
        />
      )}

      {processed && viewerType === AUDIO_VIEWER && (
        <AudioViewerComponent
          asset={asset}
          onAnalyticsChange={onAnalyticsChange}
        />
      )}

      {processed && [HTML_VIEWER, CLEVERSTORY_VIEWER].includes(viewerType) && (
        <HTMLViewerComponent
          asset={asset}
          onAnalyticsChange={onAnalyticsChange}
          recordSessionAnalytics={recordSessionAnalytics}
        />
      )}

      {processed && viewerType == SPREADSHEET_VIEWER && (
        <DefaultViewer asset={asset} onAnalyticsChange={onAnalyticsChange} recordSessionAnalytics={recordSessionAnalytics} />
      )}

      {processed && (unknownViewer || viewerType == "unknown") && (
        <DefaultViewer asset={asset} onAnalyticsChange={onAnalyticsChange} recordSessionAnalytics={recordSessionAnalytics} />
      )}
    </div>
  );
};

interface AssetProcessingStatusProps {
  asset: Asset;
  icon: string;
  message: string;
}

const AssetProcessingStatus: React.FC<AssetProcessingStatusProps> = ({ asset }) => {
  const dispatch = useDispatch();
  const timerRef = React.useRef<any>(null);
  const [timeElapsed, setTimeElapsed] = React.useState(0);

  const status = useMemo(() => {
    return _.get(asset, "status") || "";
  }, [asset]);

  const processing = useSelector((state) => {
    const assetProcessing = _.get(state, "asset.process.processing", false);
    const assetMergeStatus = _.get(state, "asset.merge", {});
    const selectedAssetId = _.get(state, "asset.selectedAssetId", null);

    if (assetProcessing) {
      return true;
    }

    if (_.size(assetMergeStatus) > 0 && _.get(assetMergeStatus, "status") !== "OK") {
      return true;
    }

    if (_.size(selectedAssetId) > 0 && !assetProcessing) {
      return true;
    }

    const preparing = _.get(state, "asset.preparing", false);
    if (preparing) {
      return true;
    }

    return false;
  });

  const assetProcessing = useSelector((state) => {
    return _.get(state, "asset.process.processing", false);
  });

  const twoMinutesElapsed = useMemo(() => {
    return timeElapsed > 2 * 10; // 2 minutes delay
  }, [timeElapsed]);

  const processingDelayed = useMemo(() => {
    return timeElapsed > 5 * 10; // 5 seconds delay
  }, [timeElapsed]);

  const icon = useMemo(() => {
    if (processingDelayed) {
      return "/images/assets/404error_message.svg";
    }

    return "/images/assets/notification_empty_message.svg";
  }, [twoMinutesElapsed, processingDelayed]);

  const message = useMemo(() => {
    if (twoMinutesElapsed) {
      return "Just a bit longer... Your file is taking time to process.";
    }

    if (processingDelayed) {
      return "Uh-oh, something’s off. We couldn’t process your file.";
    }

    return "Hang tight! Your file is processing...";
  }, [twoMinutesElapsed, processingDelayed]);

  const handleTriggerProcessing = () => {
    if (!processing && status !== "OK") {
      dispatch(triggerAssetProcessing(asset!.id));
      setTimeElapsed(0);
    }
  }

  useEffect(() => {
    if (processing && status !== "OK") {
      timerRef.current = setInterval(() => {
        console.log("Processing...");
        setTimeElapsed((prev) => prev + 10);
      }, 10000); // 10 seconds delay
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [processing, processingDelayed, asset]);

  return (
    <div className={styles.processingErrorMessage}>
      <div className={styles.inner}>
        <img className={styles.icon} src={icon} alt="error" />
        <p className={styles.message}>{message}</p>

        {processingDelayed && (
          <button disabled={assetProcessing}
            onClick={handleTriggerProcessing}
            className={styles.errorButton}>
            {assetProcessing ? 'Processing...' : 'Try Again'}
          </button>
        )}
      </div>
    </div>
  );
}


interface DocumentViewerComponentProps {
  asset: any;
  preferredWidth?: number;
  offset?: number;
  isSidePanelOpen?: boolean;
  processingAsset: boolean;
  initialPage: number;
  className?: string;
  scrollParent?: string;

  preview?: {
    loading: boolean;
    item: PreviewAsset | null;
  };

  fetchPreviewData?: () => void;
  // eslint-disable-next-line no-unused-vars
  recordSessionAnalytics?: (data: any) => void;
  // eslint-disable-next-line no-unused-vars
  onAnalyticsChange?: (data: any) => void;
}

const DocumentViewerComponent: React.FC<DocumentViewerComponentProps> = ({
  asset,
  preferredWidth,
  offset,
  preview,
  processingAsset,
  initialPage,
  className,
  scrollParent,
  fetchPreviewData,
  recordSessionAnalytics,
  onAnalyticsChange,
}) => {
  const [width, setWidth] = useState(1024);
  const [error, setError] = useState(false);

  const desktop = useMediaQuery({ query: "(min-width: 992px)" });

  const sourceUrl = useMemo(() => {
    let sourceUrl = asset.previewUrl;
    const source = _.get(asset, "source");
    const isCloudFrontUrl = _.includes(sourceUrl, "cloudfront.net");
    if (source && !isCloudFrontUrl) {
      sourceUrl = `${sourceUrl}&access_token=${AuthService.getAccessToken()}`;
    }

    return sourceUrl;
  }, [asset]);

  const handleError = (e) => {
    const hasError = preview?.item == null || e.name === "InvalidPDFException";
    if (hasError) {
      setError(true);
    }
  };

  const onPageChange = (page) => {
    recordSessionAnalytics?.({
      type: 101,
      page: page,
      eventDate: new Date().getTime()
    });
  };

  useEffect(() => {
    const offset = desktop ? 50 : 10;
    let widthValue = (width || 1024) - 2 * offset;
    widthValue = Math.min(widthValue, 1024);
    setWidth(widthValue);
  }, [preferredWidth, desktop]);

  if (error) {
    return <DefaultViewer asset={asset} onAnalyticsChange={onAnalyticsChange} recordSessionAnalytics={recordSessionAnalytics} />;
  }

  return (
    <div className={cx(styles.viewer, className)}>
      {(_.get(preview, "item.pages") || !preview?.item) && (
        <Suspense fallback={<div></div>}>
          <DocumentViewer
            id={`asset_viewer_${asset.id}`}
            loading={((preview?.loading) || processingAsset)}
            loadingComponent={() => {
              return (
                <div className={styles.loading}>
                  <img src={'/images/loading_plane.gif'} />
                  <div className={styles.message}>Loading viewer...</div>
                </div>
              );
            }}
            initialPage={initialPage}
            sourceUrl={sourceUrl}
            previewData={preview?.item}
            fetchPreviewData={fetchPreviewData}
            scrollParent={scrollParent || `.${styles.viewer}`}
            offset={offset || 0}
            preferredWidth={width}
            workerUrl={`https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js`}
            onPageChange={(page) => onPageChange(page)}
            onAnalyticsChange={onAnalyticsChange}
            disabled={false}
            onError={handleError}
          />
        </Suspense>
      )}
    </div>
  );
};

interface VideoViewerProps {
  asset: Asset;

  // eslint-disable-next-line no-unused-vars
  onAnalyticsChange?: (data: any) => void;

  // eslint-disable-next-line no-unused-vars
  recordSessionAnalytics?: (data) => void;
}

const VideoViewerComponent: React.FC<VideoViewerProps> = ({ asset, recordSessionAnalytics, onAnalyticsChange }) => {
  const videoUrl = AuthService.getURL(
    `api/2.0/assets/${asset.id}/download`, {
    modifiedDate: asset.metadata.modified_date,
    preview: true,
  }
  );

  const iconUrl = _.get(asset, "icon.full");
  const poster = _.get(asset, "poster");

  let videoProperties = null;

  if (asset.viewerProperties !== null) {
    const source = asset.viewerProperties.sources.find((properites) => {
      if (properites.type === "application/vnd.apple.mpegurl") {
        return properites;
      }
    })

    if (!_.isEmpty(source)) {
      videoProperties = source;
    }
  }

  const subtitleEnabled = asset.viewerProperties?.subtitleEnabled || false;
  const caption = asset.viewerProperties?.caption || null;

  const handleAnalyticsChange = (data) => {
    console.log("Video analytics data: ", data);
    const duration = _.get(data, "duration") || 0;

    if (onAnalyticsChange) {
      onAnalyticsChange(data);
    }

    if (duration > 0) {
      recordSessionAnalytics?.({
        type: 108,
        eventDate: new Date().getTime(),
      });
    }
  };

  return (
    <Suspense fallback={<div className={styles.videoViewer}></div>}>
      <VideoViewer
        id={`asset_viewer_${asset.id}`}
        className={cx(styles.videoViewer)}
        mode="light"
        src={videoUrl}
        videoProperties={videoProperties}
        metadata={asset.metadata}
        active={true}
        icon={iconUrl}
        poster={poster}
        onPlay={() => {
          console.log("Video started playing");
          //recordSessionAnalytics?.();
        }}
        onAnalyticsChange={handleAnalyticsChange}
        subtitleEnabled={subtitleEnabled}
        caption={caption}
      />
    </Suspense>
  );
};

interface ImageViewerProps {
  asset: Asset;
  // eslint-disable-next-line no-unused-vars
  onAnalyticsChange?: (data: any) => void;

  // eslint-disable-next-line no-unused-vars
  recordSessionAnalytics?: (request: any) => void;
}

const ImageViewerComponent: React.FC<ImageViewerProps> = ({ asset, onAnalyticsChange, recordSessionAnalytics }) => {
  const sourceUrl = useMemo(() => {
    let url = AuthService.getURL(`api/2.0/assets/${asset.id}/download`, {
      modifiedDate: asset.metadata.modified_date,
      preview: true,
    });

    if (asset.previewUrl) {
      url = asset.previewUrl;
    }

    return url;
  }, [asset]);

  const handleAnalyticsChange = (data) => {
    const duration = _.get(data, "duration") || 0;
    onAnalyticsChange?.({
      duration: duration,
      percentageRead: 100,
    });

    if (duration > 0) {
      recordSessionAnalytics?.({
        type: 108,
        eventDate: new Date().getTime(),
      });
    }
  };

  return (
    <Suspense fallback={<div className={styles.imageViewer}></div>}>
      <ImageViewer
        id={`asset_viewer_${asset.id}`}
        className={styles.imageViewer}
        mode="light"
        src={sourceUrl}
        scrollParent={`.${styles.assetViewer}`}
        loadingComponent={() => {
          return (
            <div className={cx(styles.loader)}>
              <label className={styles.message}>Loading Image....</label>
            </div>
          );
        }}
        active={true}
        onAnalyticsChange={handleAnalyticsChange}
      />
    </Suspense>
  );
};

interface ScromViewerProps {
  asset: Asset;
  // eslint-disable-next-line no-unused-vars
  onAnalyticsChange?: (data: any) => void;

  // eslint-disable-next-line no-unused-vars
  recordSessionAnalytics?: (request: any) => void;
}

const ScromViewerComponent: React.FC<ScromViewerProps> = ({ asset, onAnalyticsChange, recordSessionAnalytics }) => {
  const id = _.get(asset, 'id');
  const url = _.get(asset, "previewUrl");

  const handleAnalyticsChange = (data) => {
    const duration = _.get(data, "duration") || 0;
    onAnalyticsChange?.({
      duration: duration,
      percentageRead: 100,
    });

    if (duration > 0) {
      recordSessionAnalytics?.({
        type: 108,
        eventDate: new Date().getTime(),
      });
    }
  };

  return (
    <Suspense fallback={<div className={styles.htmlViewer}></div>}>
      <ScormViewer
        id={id}
        className={styles.htmlViewer}
        url={url}
        active={true}
        onAnalyticsChange={handleAnalyticsChange}
      />
    </Suspense>
  );
}

interface AudioViewerProps {
  asset: Asset;
  // eslint-disable-next-line no-unused-vars
  onAnalyticsChange?: (data: any) => void;

  // eslint-disable-next-line no-unused-vars
  recordSessionAnalytics?: (request: any) => void;
}

const AudioViewerComponent: React.FC<AudioViewerProps> = ({ asset, onAnalyticsChange, recordSessionAnalytics }) => {
  const sourceUrl = useMemo(() => {
    let url = AuthService.getURL(`api/2.0/assets/${asset.id}/download`, {
      modifiedDate: asset.metadata.modified_date,
      preview: true,
    });

    if (asset.previewUrl) {
      url = asset.previewUrl;
    }

    return url;
  }, [asset]);

  const iconUrl = _.get(asset, "icon.thumbnail", AlbumCover);
  const poster = _.get(asset, "poster");

  const handleAnalyticsChange = (data) => {
    const duration = _.get(data, "duration") || 0;
    onAnalyticsChange?.({
      duration: duration,
      percentageRead: 100,
    });

    if (duration > 0) {
      recordSessionAnalytics?.({
        type: 108,
        eventDate: new Date().getTime(),
      });
    }
  };

  return (
    <Suspense fallback={<div className={styles.audioViewer}></div>}>
      <AudioViewer
        id={`asset_viewer_${asset.id}`}
        className={cx(styles.audioViewer)}
        src={sourceUrl}
        metadata={asset.metadata}
        active={true}
        icon={iconUrl}
        poster={poster}
        onPlay={() => {
          console.log("Audio started playing");
        }}
        onAnalyticsChange={handleAnalyticsChange}
      />
    </Suspense>
  );
};

interface HTMLViewerComponentProps {
  asset: Asset;
  // eslint-disable-next-line no-unused-vars
  onAnalyticsChange?: (data: any) => void;

  // eslint-disable-next-line no-unused-vars
  recordSessionAnalytics?: (request: any) => void;
}

const HTMLViewerComponent: React.FC<HTMLViewerComponentProps> = ({ asset, onAnalyticsChange, recordSessionAnalytics }) => {
  const name = _.get(asset, "name");
  const type = _.get(asset, "viewer") as string;
  let url = _.get(asset, "metadata.url");
  let mode = _.get(asset, "viewerMode");
  let optimizedUrl = null;

  const preview = null;

  if (type === CLEVERSTORY_VIEWER && _.get(asset, "cleverstoryViewerMode")) {
    url = _.get(asset, "previewUrl");
    mode = _.get(asset, "cleverstoryViewerMode");

  } else if (mode === RENDER_IN_VIEWER) {
    url = _.get(asset, "metadata.url");

  } else if (mode === RENDER_OPTIMIZED_VIEW) {
    optimizedUrl = _.get(preview, "htmlPreview") || null;

  }
  const iconUrl = _.get(asset, "icon.key") || "/images/invalidFileType/url.jpg";
  const canPreview = _.get(asset, "preview");

  const handleAnalyticsChange = (data) => {
    const duration = _.get(data, "duration") || 0;
    onAnalyticsChange?.({
      duration: duration,
      percentageRead: 100,
    });

    if (duration > 0) {
      recordSessionAnalytics?.({
        type: 108,
        eventDate: new Date().getTime(),
      });
    }
  };

  return (
    <Suspense fallback={<div className={styles.htmlViewer}></div>}>
      <HTMLViewer
        id={`asset_viewer_${asset.id}`}
        className={styles.htmlViewer}
        name={name}
        icon={iconUrl}
        mode={mode}
        url={url}
        optimizedUrl={optimizedUrl}
        canPreview={canPreview}
        active={true}
        onAnalyticsChange={handleAnalyticsChange}
      />
    </Suspense>
  );
};

interface DefaultViewerProps {
  asset: Asset;
  // eslint-disable-next-line no-unused-vars
  onAnalyticsChange?: (data: any) => void;

  // eslint-disable-next-line no-unused-vars
  recordSessionAnalytics?: (request: any) => void;
}

const DefaultViewer = ({ asset, onAnalyticsChange, recordSessionAnalytics }: DefaultViewerProps) => {

  const icon = asset.icon ? asset.icon.thumbnail : "/images/defaultIcons/default_asset.jpg";

  const handleDownloadClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (!asset) {
      return false;
    }

    const url: any = AuthService.getURL(`api/2.0/assets/${asset.id}/download`, {
      modifiedDate: asset.metadata.modified_date,
      _: new Date().getTime(),
    });

    if (url !== null) {
      window.open(url, "_blank");
    }
  };

  const handleDurationChange = (duration: number, change: number) => {
    if (change > 0) {
      console.log("Duration changed: ", duration, change);
      onAnalyticsChange?.({
        duration: duration,
        percentageRead: 100,
      });

      recordSessionAnalytics?.({
        type: 108,
        eventDate: new Date().getTime(),
      });
    }
  }

  useEffect(() => {
    onAnalyticsChange?.({
      duration: 0,
      percentageRead: 100,
    });
  }, []);

  return (
    <ElementTimerComponent id={asset.id} syncDuration={30} onDurationChange={handleDurationChange}>
      <div className={styles.defaultViewer}>
        <div className={styles.icon}>
          <img src={icon} />
        </div>
        <div className={styles.message}>Click here to download this file</div>
        <div className={styles.name}>{asset.name}</div>
        <a onClick={(e) => handleDownloadClick(e)} className={styles.downloadButton}>Download</a>
      </div>
    </ElementTimerComponent>
  );
};